import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { useContextualMenu } from '@databases/contextual-menu';
import { useDatabaseContent } from '@databases/database-state';
import { DragType } from '@databases/drag';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { isModifierKeyPressed, useFocusInputField } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectFirstSelectedValue } from '@databases/redux/cellSelections/selectors';
import { doClearValues } from '@databases/redux/contextual-menu/actions';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import { type Participant } from '@databases/utils/presence/types';
import { sleep } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import React, {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

export const InlineEditContainer = styled.form.attrs({ action: '#' })<{
  isEditing?: boolean;
  isManual?: boolean;
  preserveEditWidth?: boolean;
  hasThreads?: boolean;
  participants: Participant[];
  isReadOnly: boolean;
  isSelected?: boolean;
  collaboratorColor?: string;
}>`
  ${({ isSelected, isReadOnly, collaboratorColor }) => {
    const borderColor =
      isSelected || isReadOnly
        ? undefined
        : // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
          collaboratorColor && token(collaboratorColor);

    return (
      borderColor &&
      css`
        border: 2px solid ${borderColor};
        border-radius: 3px;
      `
    );
  }}

  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  padding-right: ${(props) => (props.hasThreads ? '36px' : '0')};

  ${(props) =>
    props.isEditing || props.isManual
      ? ''
      : css`
          transition: background 0.2s;

          &:hover {
            background: ${token('elevation.surface.hovered', '#F1F2F4')};
          }
        `}
  > div {
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    > * {
      flex-grow: 1;
    }
  }

  > :nth-child(${(props) => (props.isEditing ? 1 : 2)}) {
    ${({ preserveEditWidth }) =>
      preserveEditWidth
        ? css`
            visibility: hidden; // not using "display: none" to preserve the width of the element for both modes
          `
        : css`
            display: none;
          `}
  }
`;

export interface InlineEditStatelessProps<V> {
  readView: () => ReactNode;
  editView: () => ReactNode;
  isEditing: boolean;
  isReadOnly: boolean;
  preserveEditWidth?: boolean;
  onIsEditingChange: (isEditing: boolean) => void;
  /** If true, the edit mode cannot be activated (but deactivated) by the user, but only programmatically by changing the isEditing prop. */
  isManual?: boolean;

  /**
   * The current value of the field. When the user resets the field (by pressing escape), the latest value when the field was switched to
   * edit mode is passed to the onReset prop.
   */
  value: V;
  onReset: (value: V) => void;
  onSubmit?: (value: V) => void;
  onBlur?: (value: V) => void;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  enableSelection?: boolean;
  tooltipContent?: ReactNode;
  isSelected?: boolean;
  field?: DatabaseField<DatabaseFieldType>;
  entryId?: string;
  hasThreads?: boolean;
  shouldShowHoverButton?: boolean;
}

const MissingConfigurationTooltip = ({
  children,
  tooltipContent,
}: {
  children: ReactNode;
  tooltipContent?: ReactNode;
}) =>
  tooltipContent ? (
    <Tooltip content={tooltipContent}>{children}</Tooltip>
  ) : (
    <>{children}</>
  );

export function InlineEditStateless<V>({
  readView,
  editView,
  isEditing,
  preserveEditWidth,
  onIsEditingChange,
  isManual,
  value,
  onReset,
  onSubmit,
  onBlur,
  onIsSelectedChange,
  enableSelection,
  tooltipContent,
  isSelected,
  field,
  entryId,
  hasThreads = false,
  shouldShowHoverButton,
  isReadOnly,
}: InlineEditStatelessProps<V>): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusInputField(
    isEditing && containerRef.current ? containerRef.current : undefined
  );

  const { closeMenu, isMenuOpen } = useContextualMenu();
  const { view } = useView();
  const { contentId } = useDatabaseContent();
  const { openThread } = useCommentStateContext();

  const store = defaultStoreUtils.useStore();

  /**
   * Selects the cell initially when the user starts to drag the drag caret to fill values or adds the cell to the selection on hover and
   * drag. Also set dragging state to show the drag to fill indicator.
   **/
  useEffect(() => {
    /*
     * Select text inside a text area which is a child of a draggable
     * element doesn't work in Firefox: https://bugzilla.mozilla.org/show_bug.cgi?id=800050
     * Workaround is to set draggable to "false" for the text area when it is being edited
     * */
    if (!containerRef.current || isEditing || !enableSelection) {
      return;
    }
    return combine(
      draggable({
        element: containerRef.current,
        getInitialData: () => ({
          type: DragType.SELECT_VALUES,
        }),
        onDrop: () => {
          preventUnhandled.stop();
        },
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          // disable the native drag preview
          disableNativeDragPreview({ nativeSetDragImage });
          // we don't want any native drop animation for when the user
          // does not drop on a drop target. we want the drag to finish immediately
          preventUnhandled.start();
        },
      }),
      dropTargetForElements({
        element: containerRef.current,
        onDragStart: ({ source }) => {
          if (source.data.type === DragType.SELECT_VALUES) {
            onIsSelectedChange?.();
          }
        },
        onDragEnter: ({ source }) => {
          if (source.data.type === DragType.SELECT_VALUES && !isSelected) {
            // Fixes issue where first cell has white background when performing multiselect
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }

            removeTextSelection();
            onIsSelectedChange?.({ isMultiSelectOn: true });
          }
        },
      })
    );
  }, [containerRef, enableSelection, isEditing, isSelected, onIsSelectedChange]);

  const isShiftKeyPressed = useRef(false);
  const [isUsingIME, setIsUsingIME] = useState(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const commentThreadsPopup = document.getElementById('comment-threads-popup');

      if (commentThreadsPopup && commentThreadsPopup.contains(target)) {
        return;
      }

      const isMultiSelectOn =
        isShiftKeyPressed.current || isModifierKeyPressed(e, ['shiftKey']);
      if (enableSelection && isMultiSelectOn) {
        e.preventDefault();
        onIsSelectedChange?.({ isMultiSelectOn });
      } else if (
        !isMultiSelectOn &&
        (!isEditing || field?.type === DatabaseFieldType.ATTACHMENT) &&
        !isManual &&
        !(
          e.target instanceof Element &&
          e.target.closest('a[href],input,button,select,textarea,area[href]')
        )
      ) {
        // Wait for selection to be updated and only switch to editing mode if nothing is selected. This prevents switching
        // to edit mode when the click event was triggered by a user selecting text.
        await sleep(0);
        if (getSelection()?.type !== 'Range') {
          if (
            isSelected ||
            (e.target instanceof Element && e.target.closest('th')) || // don't double click to edit field names
            (e.target instanceof Element && e.target.closest('h1')) // don't double click to edit database title
          ) {
            onIsEditingChange(true);
          }
          closeMenu();
          if (enableSelection) {
            onIsSelectedChange?.();
          }
        }
      }
    },
    [
      enableSelection,
      isEditing,
      field?.type,
      isManual,
      onIsSelectedChange,
      isSelected,
      closeMenu,
      onIsEditingChange,
    ]
  );

  const handleFocus = useCallback(() => {
    if (!isEditing) {
      onIsEditingChange(true);
      closeMenu();

      if (enableSelection) {
        onIsSelectedChange?.();
      }
    }
  }, [closeMenu, enableSelection, isEditing, onIsEditingChange, onIsSelectedChange]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      isShiftKeyPressed.current = false;
      if (!e.currentTarget.contains(e.relatedTarget)) {
        e.stopPropagation();
        onIsEditingChange(false);
        onBlur?.(value);
      }
    },
    [onBlur, onIsEditingChange, value]
  );

  const handleCompositionStart = useCallback(() => {
    setIsUsingIME(true);
  }, []);

  const handleCompositionEnd = useCallback(() => {
    setIsUsingIME(false);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      if (!isUsingIME) {
        blurActiveElement(); // Removing this may prevent database name from being updated
      }
      e.preventDefault();
      onSubmit?.(value);
    },
    [isUsingIME, onSubmit, value]
  );

  const previousValue = useRef(value);
  useEffect(() => {
    if (isEditing) {
      // Backup value when switching to edit mode so that we can revert to this value.
      previousValue.current = value;
    }
  }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onReset(previousValue.current);
        onIsEditingChange(false);
        blurActiveElement();
      }
      if (e.key === 'Shift') {
        isShiftKeyPressed.current = true;
      }
    },
    [onIsEditingChange, onReset]
  );

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Shift') {
      isShiftKeyPressed.current = false;
    }
  }, []);

  const commentThreadDialogOpen = useMemo(
    () => openThread?.valueId !== undefined,
    [openThread.valueId]
  );

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isPasteOperation = (e.ctrlKey || e.metaKey) && e.key === 'v';
      const isIgnoredKey =
        e.key === 'Backspace' ||
        e.key === 'Escape' ||
        e.key === 'Shift' ||
        e.ctrlKey ||
        e.metaKey ||
        isPasteOperation;

      if (
        !contentId ||
        !isSelected ||
        isIgnoredKey ||
        isMenuOpen ||
        commentThreadDialogOpen
      ) {
        return;
      }
      const firstSelectedValue = selectFirstSelectedValue(store.getState());
      const isCurrentFieldFirstSelected =
        (firstSelectedValue?.entryId === entryId &&
          firstSelectedValue?.fieldId === field?.id) ||
        view.settings.layout.type !== DatabaseLayoutType.TABLE;

      if (isCurrentFieldFirstSelected) {
        onIsEditingChange(true);
        onIsSelectedChange?.();
      }
    };

    if (isSelected) {
      window.addEventListener('keydown', handleKeyPress);
    } else {
      window.removeEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    commentThreadDialogOpen,
    contentId,
    entryId,
    field?.id,
    isMenuOpen,
    isSelected,
    onIsEditingChange,
    onIsSelectedChange,
    openThread.valueId,
    store,
    view.settings.layout.type,
  ]);

  const isDatabasePresenceEnabled = getExperimentValue('databasePresence', 'enabled', {
    fireExperimentExposure: false,
  });

  const shouldShowTelepointers =
    !isSelected && !isReadOnly && isDatabasePresenceEnabled && field?.id && entryId;

  const { participants, recentCollaboratorColor } = usePresence({
    grouping: field?.id ? { fieldId: field.id, entryId } : undefined,
    componentId: 'InlineEdit',
    isEnabled: Boolean(shouldShowTelepointers),
  });

  return (
    <InlineEditContainer
      preserveEditWidth={preserveEditWidth}
      isEditing={isEditing}
      isManual={isManual}
      onBlur={handleBlur}
      onClick={handleClick}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      ref={containerRef}
      hasThreads={hasThreads}
      collaboratorColor={recentCollaboratorColor}
      isReadOnly={isReadOnly}
      isSelected={isSelected}
    >
      {shouldShowTelepointers && containerRef && shouldShowHoverButton && (
        <PresenceNameplate participants={participants} anchorRef={containerRef} />
      )}
      <MissingConfigurationTooltip tooltipContent={tooltipContent}>
        {contentId && (
          <ContentView
            contentId={contentId}
            isSelected={Boolean(isSelected)}
            isEditing={isEditing}
            isReadOnly={isReadOnly}
          >
            {readView()}
          </ContentView>
        )}
      </MissingConfigurationTooltip>
      {isEditing && editView()}
      <input type="submit" style={{ display: 'none' }} />
      {!isEditing && !isManual && <span tabIndex={0} onFocus={handleFocus} />}
    </InlineEditContainer>
  );
}

const ContentView = ({
  children,
  contentId,
  isSelected,
  isEditing,
  isReadOnly,
}: {
  children: ReactNode;
  contentId: string;
  isSelected: boolean;
  isEditing: boolean;
  isReadOnly: boolean;
}) => {
  const dispatch = defaultStoreUtils.useDispatch();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isReadOnly && !isEditing && e.key === 'Backspace' && isSelected) {
        const target = e.target as HTMLElement;
        const commentThreadsPopup = document.getElementById('comment-threads-popup');

        if (commentThreadsPopup && commentThreadsPopup.contains(target)) {
          return;
        }

        e.stopPropagation();
        dispatch(doClearValues(contentId));
      }
    },
    [contentId, dispatch, isEditing, isSelected, isReadOnly]
  );

  return (
    <Box
      tabIndex={0}
      onKeyDown={handleKeyDown}
      xcss={xcss({
        // TODO: Remove this once we have proper focus and focus ring in our app.
        ':focus': {
          outline: 'none',
        },
      })}
    >
      {children}
    </Box>
  );
};
